import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChange,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {MvsFormComponent} from "../../../form/component/mvs-form/mvs-form.component";
import {MvsFormControlService} from "../../../form/service/mvs-form-control.service";
import {MessageService} from "primeng/api";
import {Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, filter} from "rxjs/operators";
import IBAN from "iban";
import {MvsCrudObjectHistoryService} from "../../service/mvs-crud-object-history.service";
import {UiLogger} from "../../../form/helper/ui-logger";
import {ServerFieldError} from "./server-field-error.interface";
import {AbstractControl, ValidatorFn, Validators} from "@angular/forms";
import {FormStateService} from "../../../form/field/service/form-state.service";
import {MVS_FORM_USE_CASE_TOKEN, MvsFormUseCaseToken} from "./template-use-case-token/template-use-case.token";
import {SgUseCaseDto} from "../../../../sg/model/dto/entity/sg-use-case.dto";
import {FormSmartGuideFocusEvent} from "../../../form/dto/form-smart-guide-focus-event.dto";
import {FormFooterButton} from "./dto/dorm-custom-footer-button.interface";
import {ObjectRequestList} from "../../../dto/object/request/object-request-list";
import {MvsCoreService} from "../../../service/mvs-core.service";
import {FormModeEnum} from "../../../dto/object/data/form-mode.enum";
import {DtoDetail} from "../../../dto/dto.detail";
import {DtoImportObjectContext} from "../../../dto/dto.import.object.context";
import {MvsTemplateDirective} from "../../../templating/directive/mvs-template.directive";
import {MvsFormDto} from "../../../dto/form/dto/mvs-form.dto";
import {MvsFormControlOverwrite} from "../../../logic/form/logic/mvs-form-control-overwrite";
import {MvsCrudService} from "../../../service/crud/mvs-crud.service";
import {WidgetConfigDto} from "../../../widget/widget-config.dto";
import {WidgetHelperButton} from "../../../basic/widget-helper-button";
import {MvsFormEventCrudAction} from "../../../logic/form/logic/mvs-form-event-crud-action";
import {MvsFormEvent} from "../../../logic/form/logic/mvs-form-event";
import {MvsFormGroup} from "../../../logic/form/mvs-form-group";
import {MvsFormEventDtoToForm} from "../../../logic/form/logic/mvs-form-event-dto-to-form";
import {DtoTemplateOptions} from "../../../dto/dto-template-options";
import {DtoTemplate} from "../../../dto/dto-template";
import {FormActionEnum} from "../../../service/crud/enum/form-action.enum";
import {DtoHelper} from "../../../helper/dto-helper";
import {MvsFormEventFormToDto} from "../../../logic/form/logic/mvs-form-event-form-to-dto";
import {MvsFormFieldDto} from "../../../dto/form/dto/mvs-form-field.dto";
import {SelectChangeEvent} from "primeng/select";
import {MvsFormStateDto} from "./dto/mvs-form-state.dto";

/**
 * Input controls the user types into CHARACTER BY CHARACTER. When such a field is a
 * `refreshFormOnChange` trigger, its value changes once per keystroke, so the refresh must be
 * debounced — otherwise the form is rebuilt mid-word, the control loses focus, and the rest of the
 * input is dropped: typing "2016" into a numeric trigger field leaves "2" behind (measured).
 *
 * Only `text` used to be debounced, which is why free-text triggers behaved and numeric ones did not.
 * Discrete controls (select, checkbox, date pickers) are deliberately NOT listed: they emit one
 * change per user decision, and delaying those would make the form feel unresponsive.
 */
const TYPED_INPUT_CONTROLS: ReadonlySet<string> = new Set([
    'text', 'textarea', 'numberInt', 'amount', 'duration',
    // 'iban': cr.CustomerBankAccount marks the IBAN refreshFormOnChange (the server resolves the bank
    // from it). Undebounced, every keystroke rebuilt the form and recreated the IBAN control mid-input.
    // no ticket: the debounce alone is not enough — users type an IBAN in 4-character blocks with
    // pauses longer than REFRESH_DEBOUNCE_MS, and a mid-input refresh rebuilds the form from a dto
    // snapshot, dropping every keystroke typed during the round trip. The server can only resolve the
    // bank from a COMPLETE IBAN anyway, so the iban arm is additionally gated by isRefreshWorthyValue():
    // it refreshes only for a valid IBAN or an emptied field (so the server may clear the bank).
    'iban'
]);

/**
 * no ticket: decides whether a debounced value of a typed trigger field may drive a template refresh.
 *
 * <p>Only the {@code iban} control is gated: a partial or invalid IBAN never refreshes (the server cannot
 * resolve a bank from it, and the rebuild would swallow the keystrokes typed meanwhile). An empty value
 * (whitespace stripped, null/undefined included) does refresh — the user cleared the field. Every other
 * control type passes unchanged.
 */
export function isRefreshWorthyValue(uiInputControl: string, value: unknown): boolean {
    if (uiInputControl !== 'iban') {
        return true;
    }
    const stripped = value == null ? '' : String(value).replace(/\s+/g, '');
    return stripped.length === 0 || IBAN.isValid(stripped);
}

/**
 * How long a typed trigger field stays quiet before the form is refreshed.
 *
 * This is a "the user stopped typing" threshold, not a rendering delay, so it has to be longer than
 * the gap between two keystrokes within one word or number. At the previous 300 ms a normal pause
 * mid-year already fired a round trip, which rebuilt the form while the user was still in the field.
 */
const REFRESH_DEBOUNCE_MS = 800;

@Component({
    selector: 'mvs-crud-object-form',
    templateUrl: './mvs-crud-object-form.component.html',
    standalone: false
})
export class MvsCrudObjectFormComponent implements OnInit, OnDestroy, OnChanges {

    sgUseCaseService: MvsCrudService;
    constructor(
        @Inject(MVS_FORM_USE_CASE_TOKEN) private config: MvsFormUseCaseToken,
        protected coreService: MvsCoreService,
        protected formService: MvsFormControlService,
        private messageService: MessageService,
        protected objectHistory: MvsCrudObjectHistoryService,
        private formStateService: FormStateService,
        private elementRef: ElementRef) {
    }

    /**
     * Where the caret was when a refresh started, so it can be put back afterwards.
     *
     * A trigger-driven refresh rebuilds the whole form group, which destroys and recreates every input
     * element — so the field the user is typing in loses focus mid-word. Debouncing makes that rare
     * instead of per-keystroke, but "rare" is still every typing pause, which is exactly when a user is
     * still working in the field.
     */
    private pendingFocus: { fieldId: string; selectionStart: number; selectionEnd: number } | null = null;

    /**
     * Remembers the focused input and its caret, keyed by FORM FIELD ID.
     *
     * The id comes from the field's own `<label [for]>`, which the field wrapper renders — the element
     * ids PrimeNG generates (`pn_id_46`) are regenerated on every rebuild and cannot be matched.
     */
    private captureFocusedField(): void {

        this.pendingFocus = null;

        const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;

        if (!active || !this.elementRef?.nativeElement?.contains(active)) {
            return;
        }

        if (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA') {
            return;
        }

        // The label sits in the same field container as the control it describes.
        const container = active.closest('.field') || active.closest('[class*="field"]');
        const fieldId = container?.querySelector('label[for]')?.getAttribute('for');

        if (!fieldId) {
            return;
        }

        this.pendingFocus = {
            fieldId,
            selectionStart: active.selectionStart ?? active.value?.length ?? 0,
            selectionEnd: active.selectionEnd ?? active.value?.length ?? 0
        };
    }

    /**
     * Puts focus and caret back after the rebuild.
     *
     * Deferred with a timeout because the new control does not exist until Angular has rendered the
     * rebuilt form group. Silently does nothing when the field is gone — a form logic may well have
     * hidden the very field that triggered the refresh.
     */
    private restoreFocusedField(): void {

        const pending = this.pendingFocus;
        this.pendingFocus = null;

        if (!pending) {
            return;
        }

        setTimeout(() => {
            const host = this.elementRef?.nativeElement as HTMLElement;
            const label = host?.querySelector(`label[for="${pending.fieldId}"]`);
            const container = label?.closest('.field') || label?.parentElement?.parentElement;
            const input = container?.querySelector('input, textarea') as HTMLInputElement | null;

            if (!input || input.disabled || input.readOnly) {
                return;
            }

            input.focus();

            // Only text-like inputs expose a selection range; a number input throws on some browsers.
            try {
                input.setSelectionRange(pending.selectionStart, pending.selectionEnd);
            } catch {
                // caret restore is a nicety - focus alone already keeps the user typing
            }
        });
    }

    @Input() importObjectContextDto: DtoImportObjectContext;
    @Input() templates: QueryList<MvsTemplateDirective>;

    @Input() dto: DtoDetail;
    @Input() form: MvsFormDto;
    @Input() formControlOverwrite: MvsFormControlOverwrite;
    @Input() defaultCreateDto: DtoDetail;

    @Input() createMode: boolean;
    @Input() crudService: MvsCrudService;

    @Input() widgetLayoutConfig: WidgetConfigDto;

    @Input() formFooterCustomButtons: FormFooterButton[] = [];

    /**
     * Suppress the built-in footer action buttons ("Anlegen" in create mode, "Speichern" and the
     * delete action in edit mode) so an embedding component can own the save gate itself via
     * {@link #formFooterCustomButtons}.
     *
     * Additive and OFF by default: while this is false the footer renders exactly as before.
     */
    @Input() hideDefaultActions: boolean = false;

    /**
     * Suppress the "Änderungshistorie" icon in the footer — the control that opens the revision
     * panel for the edited row via {@link #onShowHistory}.
     *
     * For a form embedded in a host screen whose subject is the PROCESS, not the row: the row's
     * revision list is not what the user is working on there. Read-only either way — hiding it
     * removes no writer and no validation.
     *
     * Additive and OFF by default: while this is false the footer renders exactly as before.
     */
    @Input() hideHistoryAction: boolean = false;

    /**
     * Suppress the field-audit icon in the footer — the control that toggles {@link #showAudit},
     * i.e. the per-field audit information rendered inside the form.
     *
     * Same reasoning and the same read-only nature as {@link #hideHistoryAction}.
     *
     * Additive and OFF by default: while this is false the footer renders exactly as before.
     */
    @Input() hideAuditAction: boolean = false;

    /**
     * Do not render the footer ACTION BAR at all — every control in it: the audit, history and delete
     * icons, "Anlegen" / "Speichern", {@link #formFooterCustomButtons}, {@link #formFooterButtons} and
     * "Back". For a form embedded in a host that owns every action itself, where even an empty bar
     * would still take its padding under the form.
     *
     * Rendering only: the write gate, {@link #triggerAction} and the soft-delete banner are unchanged.
     *
     * Additive and OFF by default: while this is false the footer renders exactly as before.
     */
    @Input() hideActionBar: boolean = false;

    @Input() readonly: boolean = false;
    @Input() busy: boolean = false;
    @Input() disabled: boolean;
    @Input() enableBack: boolean = false;
    @Input() objectType: string;
    @Input() removeDelete: boolean = false;
    @Input() sgUseCaseId: number;
    @Input() formErrors: ServerFieldError[] = [];
    @Input() formMode: FormModeEnum;
    @Input() forceHideModes: boolean;
    @Input() autoSaveMode: boolean = false;

    /**
     * How long the form has to stand still before {@link #autoSaveMode} writes it — the debounce of
     * {@link #initAutosave}.
     *
     * Exists because the still-stand is a property of the SURFACE, not of the framework: a host whose
     * own save lane already runs on a different rhythm would otherwise have its embedded forms saving
     * out of step with everything around them.
     *
     * Additive and unchanged by default: 1000 is the literal this pipe carried before, so a consumer
     * that does not bind it debounces exactly as it did. Read ONCE, in `ngOnInit` — the subscription is
     * opened there, so a later change of the value does not move a running form.
     */
    @Input() autoSaveDebounceMs: number = 1000;

    /**
     * @ticket TM-1000002003
     * Answer a saved row and a changed {@link #formControlOverwrite} by UPDATING the live form group
     * instead of rebuilding it, so the input being typed in is never destroyed. Both otherwise end in
     * {@code refreshComponent()}: a new group, new controls, a fresh `<input>` per field. Still
     * rebuilds for a different row id, a new {@code form} or import context, an EXCLUDED toggle and a
     * changed field-group heading. OFF by default.
     */
    @Input() inPlaceUpdate: boolean = false;


    @Input() formFooterButtons: WidgetHelperButton[];
    @Output() onFooterButtonClick = new EventEmitter<{ event: WidgetHelperButton, action: MvsFormEventCrudAction }>();

    // TM-1000001714: When set, the footer renders an inline success p-message (with undo control)
    // in place of the regular "Löschen" icon button. Cleared by the parent generic component
    // after the user clicks the undo icon (or after the dialog is dismissed).
    @Input() softDeletedNotice: { id: number; message: string } | null = null;

    // TM-1000001714: Emitted when the user clicks the undo icon inside the inline soft-delete message.
    // Payload = id of the soft-deleted entity that should be restored.
    @Output() onUndoSoftDelete: EventEmitter<number> = new EventEmitter<number>();

    @Output() lifecycleEvents: EventEmitter<MvsFormEvent> = new EventEmitter<MvsFormEvent>();
    @Output() onBackEvent = new EventEmitter();
    @Output() onFormDirty = new EventEmitter<boolean>();
    @Output() onFormChange = new EventEmitter<DtoDetail>();

    /**
     * Whether the built-in action button WOULD currently be enabled — i.e. exactly the predicate
     * behind the "Anlegen" / "Speichern" footer buttons (form complete, not busy, not disabled,
     * formGroup valid).
     *
     * Exists so a consumer that suppresses those buttons via {@link #hideDefaultActions} can gate
     * its OWN action button on the same condition instead of re-implementing field checks.
     * Emitted only when the value changes; a consumer that does not bind it is unaffected.
     */
    @Output() onFormSubmitStateChange = new EventEmitter<boolean>();

    /**
     * Full state snapshot (dirty, valid, complete, busy, disabled, readonly, createMode,
     * submittable) — see {@link MvsFormStateDto}. Emitted once for the freshly built form group and
     * then on every real change of any of those flags.
     *
     * Superset of {@link #onFormDirty} and {@link #onFormSubmitStateChange}, both of which keep
     * their existing behaviour. Intended for a consumer that suppresses the built-in buttons via
     * {@link #hideDefaultActions} and drives the write itself via {@link #triggerAction}.
     */
    @Output() onFormStateChange = new EventEmitter<MvsFormStateDto>();

    @Output() onFormTargetFocus = new EventEmitter<FormSmartGuideFocusEvent>;
    @Output() onTemplateRefresh = new EventEmitter<MvsFormDto>();


    subscriptionFormGroupValueChange: Subscription[] = [];
    subscriptionFormGroupStateChange: Subscription[] = [];
    subscriptionsFormGroupFieldValueChange: Subscription[] = [];

    formGroup: MvsFormGroup;

    @ViewChild(MvsFormComponent) mvsFormComponent: MvsFormComponent;

    initialized: boolean = false;

    showAudit: boolean = false;
    mouseEvent: MouseEvent;

    refreshFormOnFieldChange: boolean;
    dtoValue: DtoDetail;

    //busy: boolean = false;
    complete: boolean = false;

    /** Last value published via {@link #onFormSubmitStateChange}; null = nothing published yet. */
    private lastSubmitState: boolean = null;

    /** Last value published via {@link #onFormStateChange}; null = nothing published yet. */
    private lastFormState: MvsFormStateDto = null;

    /**
     * Last value published via {@link #onFormDirty}; null = nothing published yet.
     *
     * Only used to reset the flag back to false when the form group is rebuilt (after a successful
     * save the group is fresh and therefore pristine again). The per-keystroke emit inside
     * valueChanges stays unconditional, so no existing consumer sees fewer events than before.
     */
    private lastDirtyState: boolean = null;

    formState: string;

    templateIsRefreshing: boolean = false;

    uniqueId: string;

    sgUseCases: SgUseCaseDto[];
    selectedUseCaseId: number; //only for temporary use case selection

    formModeOptions = [];
    selectedFormMode: FormModeEnum;
    busyFormMode: FormModeEnum | null = null;

    loadingHistory: boolean = false;
    autosaveTrigger = new Subject<void>();
    autosaveSub: Subscription;



    /**
     * Initialize component. We expect all relevant information to be already provided.
     */
    ngOnInit(): void {
        this.uniqueId = this.formStateService.generateUniqueId(this.objectType);

        if (this.sgUseCaseId) {
            this.selectedUseCaseId = this.sgUseCaseId;
        }

        if (this.autoSaveMode == true) {
            this.initAutosave();
        }

        // refresh component (based on provided data)
        this.refreshComponent(true);

        this.formModeOptions = this.getAvailableModes();
        this.selectedFormMode = this.formMode ?? FormModeEnum.REGULAR;
        this.onFormModeChange(this.selectedFormMode);
    }

    initAutosave() {
        this.autosaveSub = this.autosaveTrigger.pipe(
            debounceTime(this.autoSaveDebounceMs)
        ).subscribe(() => {
            this.runAutosave()
        });
    }

    private runAutosave() {

        if (!this.formGroup.valid || !this.formGroup.dirty) {
            return;
        }

        if (this.templateIsRefreshing || this.busy || this.disabled || this.readonly) {
            return;
        }

        const dto = this.formGroup.value;
        let action = null;

        if (dto?.id) {
            action = 'save';
        } else {
            action = 'create';
        }

        this.onAction(action);
    }

    /**
     * Execute finale initialization steps after data was retrieved.
     */
    initFinalize(): void {
        this.initialized = true;
    }

    /**
     * Refresh Template Data.
     */
    refreshData(init: boolean): void {

        if (!this.initialized) {

            this.initForm(this.form);

            this.initFinalize();
        }

    }

    /**
     * Initialize Form
     * @param formDto
     */
    initForm(formDto: MvsFormDto) {

        // take over form
        this.form = MvsFormDto.create(formDto)

        // take over default values (context values based on ImportObjectContext)
        this.form.copyContextValuesToDto(this.dto);

        // store complete
        this.complete = this.form.formComplete;

        // process DTO to Form
        this.lifecycleEvents.emit(new MvsFormEventDtoToForm(this.dto, this.formGroup));

    }

    /**
     * Refresh Template.
     * @param init
     * @param dto
     * @param onRefresh
     * @param options
     * @param sgUseCaseId
     */
    refreshTemplate(init: boolean, dto: DtoDetail, onRefresh: boolean, options?: DtoTemplateOptions, sgUseCaseId?: number): void {

        if (this.templateIsRefreshing) {
            return; // template is already refreshing
        }

        this.busy = true;
        this.templateIsRefreshing = true;

        // busy is set internally here, so ngOnChanges never sees it — publish it explicitly,
        // otherwise a caller-owned action button stays enabled during the template round trip.
        this.emitFormState();

        // Remember where the caret is BEFORE the round trip: the response rebuilds the form group
        // and destroys the control the user is typing in.
        this.captureFocusedField();

        if (!this.dto) {
            this.dto = new DtoDetail();
        }

        if (dto) {
            this.dto = dto;
        } else if (this.formGroup) {
            // patch values (dto -> formGroup)
            this.formGroup.patchValue(this.dto);
        }

        // set-up DtoTemplate instance for data request
        const templateDto: DtoTemplate = new DtoTemplate();
        templateDto.importObjectContext = this.importObjectContextDto;
        templateDto.dto = this.dto;

        if (onRefresh) {
            templateDto.options.formAction = FormActionEnum.on_refresh;
        } else {
            templateDto.options.formAction = FormActionEnum.on_init;
        }

        templateDto.options.useCase = this.config?.useCase || null;

        if (init) {
            templateDto.options.formMode = this.formMode;
        }


        if (options) {
            templateDto.options.activityField = options.activityField;
            templateDto.options.activity = options.activity;
            templateDto.options.formMode = options.formMode;
        }

        if (sgUseCaseId) {
            templateDto.options.sgUseCaseId = sgUseCaseId; //prefer the useCase from config mode if available
        } else if (this.sgUseCaseId) {
            // templateDto.options['sgUseCaseId'] = this.sgUseCaseId;
            templateDto.options.sgUseCaseId = this.sgUseCaseId;
        }


        // we'll refresh the template/ form
        this.crudService.template(templateDto).subscribe((res: MvsFormDto) => {

            // this.form = MvsFormDto.create(res);
            if (this.templateIsRefreshing && this.initialized) {
                this.onTemplateRefresh.emit(res);
            }

            // // TM-XXX: Verify is a lightweight activity — only `activityField` changed server-side.
            // // Patch just that value into the existing formGroup to preserve the user's unsaved
            // // drafts (e.g. personDetail.title). Do NOT rebuild the form.
            // if (options?.activity != null && options?.activityField) {
            //     this.verifyValidityField(res, options.activityField);
            //     this.busy = false;
            //     this.templateIsRefreshing = false;
            //     return;
            // }

            // @ticket TM-1000002003: one guarded call; everything new lives in the method. The old
            // handler below — including the DOM focus hack it needs — is unchanged for the flag-off
            // path and for every structural refresh.
            if (this.inPlaceUpdate && this.applyTemplateInPlace(res)) {
                return;
            }

            this.form = res;
            this.busy = false;
            this.busyFormMode = null;

            if (options && options.formMode != undefined) {
                this.formMode = options.formMode;
            }


            // 30.11.2023 added defaulting to DTO processing
            if (this.form.dtoOnCreate) {
                this.dto = DtoHelper.mergeDtos(this.dto, this.form.dtoOnCreate, this.form);
            }

            // store complete
            this.complete = this.form.formComplete;

            this.refreshComponent(init);

            this.templateIsRefreshing = false;

            this.restoreFocusedField();

        }, error => {
            this.busy = false;
            this.templateIsRefreshing = false;
            this.emitFormState();
        });


    }

    /**
     * Lightweight patch for `ConfirmationActivityEnum.verify` responses.
     * The server has updated ONLY the confirmable field (`activityField`). We take the
     * formGroup's current raw value, overwrite the target leaf with the new value from
     * `res.dtoOnCreate`, and patch the formGroup back. This preserves all other user-
     * entered (unsaved) values because we never rebuild the formGroup structure.
     *
     * Path mapping: `activityField` uses plain keys (e.g. `personDetail.x`), but
     * `rawDto` / `res.dtoOnCreate` use `Dto`-suffixed keys for sub-entities (e.g.
     * `personDetailDto.x`). Non-leaf segments therefore get a `Dto` suffix appended
     * during the walk; the leaf stays bare.
     */
    private verifyValidityField(res: MvsFormDto, activityField: string): void {
        if (!activityField || !res?.dtoOnCreate) return;

        const paths = activityField.split('.');
        const rawDto = this.formGroup.getRawValue();

        if (paths.length === 1) {
            // Leaf at root — paths[0] matches rawDto/dtoOnCreate keys directly
            rawDto[paths[0]] = res.dtoOnCreate[paths[0]];
        } else {
            // Walk into the sub-object — keys carry the "Dto" suffix
            let targetParent: any = rawDto;
            let srcParent: any = res.dtoOnCreate;
            for (let i = 0; i < paths.length - 1; i++) {
                const key = `${paths[i]}Dto`;
                targetParent = targetParent?.[key];
                srcParent = srcParent?.[key];
                if (targetParent == null || srcParent == null) return;
            }
            const leafKey = paths[paths.length - 1];
            targetParent[leafKey] = srcParent[leafKey];
        }

        this.formGroup.patchValue(rawDto, { emitEvent: false });

        // re-render the confirmable field group header (timestamp + colour)
        this.mvsFormComponent?.postRefreshData();
    }

    /**
     * Refresh Component.
     */
    refreshComponent(init: boolean): void {
        // -------------------------------------------------------------------------------- //
        // Different situations:
        //
        // 1) Create Mode (missing template)
        //      The template would be missing and therefore the FORM cannot be created. First
        //      the template needs to be retrieved, before any other step can be performed.
        //
        // 2) Template and DTO (optional) exists
        //      The form can be created based on the provided/ existing information.
        // -------------------------------------------------------------------------------- //

        // destroy existing form group (if any exists)
        this.destroyFormGroup();
        // the template needs to be loaded as we are in create mode and the template is missing
        if (!this.form && this.createMode) {
            this.refreshTemplate(true, null, false);
            return; // stop further processing as this routine will be called once the template was loaded
        }


        // this.setFormGroupValuesToForm();
        // create/ update MvsFormGroup
        this.formGroup = this.formService.buildFormGroup(this.form, this.dto, this.formControlOverwrite, this.widgetLayoutConfig, this.createMode);


        if (this.formErrors && this.formErrors.length > 0) {

            this.setFormErrors(this.formErrors);
        }

        this.formGroup.widgetLayoutConfig = this.widgetLayoutConfig;

        this.dtoValue = this.formGroup.value;

        // A rebuilt group is pristine again. After a successful save the parent hands the saved dto
        // back down, which lands here — without this the caller's dirty flag would stay true for the
        // rest of the form's life. Silent on the very first build: no consumer got a baseline event
        // before, and inventing one would fire their change handler on init.
        if (this.lastDirtyState === null) {
            this.lastDirtyState = this.formGroup.dirty;
        } else if (this.lastDirtyState !== this.formGroup.dirty) {
            this.lastDirtyState = this.formGroup.dirty;
            this.onFormDirty.emit(this.formGroup.dirty);
        }

        this.subscriptionFormGroupValueChange.push(this.formGroup.valueChanges.subscribe(value => {

            if (!this.templateIsRefreshing) {
                this.onFormGroupValueChanges(value);
                this.logChangedFormControl(value);
                this.lastDirtyState = this.formGroup.dirty;
                this.onFormDirty.emit(this.formGroup.dirty);
                this.onFormChange.emit(value);
                this.emitFormState();

                if (this.autoSaveMode) {
                    this.autosaveTrigger.next();
                }
            }

        }));

        // A form-logic `value` directive computes into the DTO and buildFormGroup() renders the result,
        // but a group that is BUILT with a value never fires valueChanges — and onFormChange hangs off
        // exactly that stream (:638-653). The subscription above is created AFTER buildFormGroup(), and
        // the only patchValue after the build is the createMode default below, so a consumer that mirrors
        // this form and persists on its own action would show the computed value and submit nothing.
        // Publish the built state ONCE, and only for a form the server marked as logic-computed: a form
        // without a value directive carries an absent/empty set and behaves bit-identically to before.
        // Deliberately NO markAsDirty()/markAsTouched(): a computed proposal is not a user edit, so it
        // must not arm autosave or the "unsaved changes" warning.
        if (init && this.form?.logicAppliedValueFields?.length) {
            this.onFormChange.emit(this.formGroup.value);
        }

        this.subscriptionFormGroupStateChange.push(this.formGroup.statusChanges.subscribe(status => {
            this.onFormGroupStateChanges(status);
        }));

        // some fields need to trigger a form refresh (as the form might change)
        this.subscribeToFormRefreshFields(this.formGroup);

        // refresh data
        this.refreshData(init);

        // only runs once when component is initialized
        if (init) {
            for (let key in this.formGroup.value) {
                // First-time initialization or force update: store the initial value
                let initialValue = this.formGroup.value[key];
                if (this.createMode) {
                    // In create mode, initial value is null
                    initialValue = null;
                }
                this.formStateService.setInitialValue(this.uniqueId, key, initialValue);
            }

            // if default create dto is there take over it
            if (this.defaultCreateDto) {
                this.formGroup.patchValue(this.defaultCreateDto);
            }
        }

        // Publish the state of the freshly built group LAST: refreshData() is what runs initForm(),
        // and that is where `complete` and `initialized` get their values. Emitting earlier would
        // publish a snapshot in which the form is not yet submittable and then never correct it,
        // because nothing else changes afterwards.
        this.emitFormState();

    }


    setFormErrors(errors: any[]) {
        for (let fieldError of errors) {

            const fieldName = fieldError.field;
            const errorMessage = fieldError.defaultMessage;
            let formControl = this.formGroup.controls[fieldName];

            if (!formControl) {
                const updatedFieldName = fieldName + 'DtoId';
                formControl = this.formGroup.controls[updatedFieldName];
            }

            if (formControl && errorMessage && fieldError.code) {
                this.applyValidatorsAndErrors(formControl, errorMessage, fieldError.code);
            }


        }
    }


    logChangedFormControl(value: DtoDetail) {
        const changedControl = this.getChangedValue(value);
        if (changedControl) {
            UiLogger.log(changedControl, 'is changed');
        }
    }


    protected subscribeToFormRefreshFields(formGroup: MvsFormGroup) {

        for (let controlsKey in formGroup.controls) {
            const control = formGroup.controls[controlsKey];

            if (control instanceof MvsFormGroup) {

                this.subscribeToFormRefreshFields(<MvsFormGroup>control);

            } else {

                const fieldById = formGroup.getFieldById(controlsKey);

                if (fieldById && fieldById.refreshFormOnChange) {
                    if (!this.subscriptionsFormGroupFieldValueChange) {
                        this.subscriptionsFormGroupFieldValueChange = [];
                    }

                    if (fieldById.uiInputControl == "dateToday") {
                        // TODO: Move this information to the Input Control Configuration/ Service
                        // this control does not have the refresh option

                    } else if (TYPED_INPUT_CONTROLS.has(fieldById.uiInputControl)) {
                        // TODO: Move the Debounce information to the Input Control configuration/ Service
                        // we need to add some debounce time
                        this.subscriptionFormGroupStateChange.push(control.valueChanges.pipe(
                            debounceTime(REFRESH_DEBOUNCE_MS),
                            // no ticket: before distinctUntilChanged, so re-typing the IBAN that was
                            // already refreshed (complete → partial → same complete) causes no second round trip
                            filter(value => isRefreshWorthyValue(fieldById.uiInputControl, value)),
                            distinctUntilChanged() // Emit only if the current value is different from the last
                        ).subscribe(value => {
                            if (!this.templateIsRefreshing) {
                                // The undebounced branch below only RAISES a flag, because the
                                // form-group-wide valueChanges fires in the same tick and consumes it.
                                // After a debounce that pairing is broken: this arrives 300 ms late, the
                                // form-group event is long gone, and the refresh would be deferred to the
                                // next unrelated edit — sending the value the user had one edit ago.
                                // Measured: typing a new year left the computed field on its old result
                                // and the request carried the field as null. So drive the refresh from
                                // here, with the CURRENT form value.
                                this.onFromGroupFieldContinueChanges(value);
                                this.onFormGroupValueChanges(this.formGroup.getRawValue());
                            }
                        }));
                    } else {
                        // subscribe to value change on field level
                        this.subscriptionFormGroupStateChange.push(control.valueChanges.subscribe(value => {
                            if (!this.templateIsRefreshing) {
                                this.onFromGroupFieldContinueChanges(value);
                            }
                        }));
                    }


                }

            }

        }

    }

    onFromGroupFieldContinueChanges(value: any) {
        this.refreshFormOnFieldChange = true;
    }

    onFormGroupStateChanges(status: any) {
        this.formState = status;

        // Validity can flip without a value change (async validators, server errors stamped onto a
        // control), and the submittable verdict is derived from it.
        this.emitFormState();
    }

    /**
     * A field marked {@code refreshFormOnChange} changed — fetch the template again so the server can
     * re-evaluate what that value implies (visibility, mandatory state, computed values).
     *
     * <h3>[R3-6] What used to sit here, and why it was wrong</h3>
     * The refresh used to be suppressed by a {@code firstChangeOnCreate} flag whose stated contract was
     * „refresh on the first change in create mode and the second change in update mode". The flag was
     * pre-cleared for {@code createMode} at the top of {@code buildFormGroup}, so in practice it only
     * ever fired in UPDATE mode — where it swallowed the user's FIRST interaction with a trigger field.
     * On a checkbox that is the whole interaction: the user ticked the box and the form did not react.
     *
     * <p>The flag was presumably meant to absorb a PROGRAMMATIC first emission. MEASURED on this
     * component, there is none in update mode: the group is BUILT with the dto (:608) before the
     * subscription exists (:631), so building emits nothing; the only {@code patchValue} calls after
     * the subscription are {@link refreshTemplate}'s (:456), which runs with
     * {@code templateIsRefreshing} already true and is therefore filtered by both arms, one with
     * {@code emitEvent: false} (:576), and the {@code defaultCreateDto} patch (:685), which is
     * create-mode only. So the flag had nothing to absorb and absorbed a user action instead.
     *
     * <h3>What the {@code initialized} guard is, and what it is NOT</h3>
     * It is DEFENCE IN DEPTH against an emission that arrives before the component has finished coming
     * up: {@link initialized} is raised by {@code initFinalize()} (:398), which {@link refreshData}
     * runs at :669 — after this subscription exists. An emission before that cannot be a user action.
     *
     * <p><b>It is not a loop guard, and calling it one would be wrong.</b> It is never reset: after the
     * first build it stands open for the rest of the component's life, so it has no say in anything
     * that happens during a REBUILD. What actually prevents refresh → rebuild → patch → refresh is
     * three other things, and they are the ones to preserve:
     * <ul>
     *   <li>{@code templateIsRefreshing} — raised at the top of {@link refreshTemplate} and lowered only
     *       when the answer has been applied; both subscription arms and this method's caller test it,
     *       so nothing a refresh does to the group can start a second one;</li>
     *   <li>{@code emitEvent: false} on the one patch that runs outside that window (:576);</li>
     *   <li>{@code refreshFormOnFieldChange} being consumed exactly once — cleared below before the
     *       refresh is dispatched, so a rebuild cannot inherit a raised flag.</li>
     * </ul>
     */
    onFormGroupValueChanges(value: any) {

        if (!this.refreshFormOnFieldChange) {
            return;
        }

        // Cleared BEFORE the refresh is dispatched, not after: refreshTemplate() runs synchronously up
        // to the request, and leaving the flag standing would let the next emission refresh again.
        this.refreshFormOnFieldChange = false;

        if (!this.initialized) {
            return;
        }

        this.refreshTemplate(false, value, true);
    }

    /**
     * Destroy current Form Group.
     */
    destroyFormGroup() {
        if (!this.formGroup) {
            return;
        }

        if (this.subscriptionFormGroupValueChange) {
            for (let subscription of this.subscriptionFormGroupValueChange) {
                subscription.unsubscribe();
            }
        }
        this.subscriptionFormGroupValueChange = [];

        if (this.subscriptionFormGroupStateChange) {
            for (let subscription of this.subscriptionFormGroupStateChange) {
                subscription.unsubscribe();
            }
        }
        this.subscriptionFormGroupStateChange = [];

        if (this.subscriptionsFormGroupFieldValueChange) {
            for (const field in this.subscriptionsFormGroupFieldValueChange) {
                this.subscriptionsFormGroupFieldValueChange[field].unsubscribe();
            }
        }
        this.subscriptionsFormGroupFieldValueChange = [];

    }


    /**
     * Process actions (SAVE, DELETE, ...)
     * @param action
     */
    onAction(action: string) {

        // do not process any actions, if one of the conditions is met
        if (this.busy || this.disabled || this.readonly || !this.complete) {
            return;
        }

        const currentDtoValue: any = this.formGroup.value;

        // inform caller
        this.lifecycleEvents.emit(new MvsFormEventFormToDto(currentDtoValue, this.formGroup));
        // process action
        switch (action) {
            case "save":
                if (this.validateForm(true)) {
                    // this.formStateService.clearInitialValues(this.objectType);
                    this.formStateService.resetChanges(this.uniqueId); // Reset changes

                    for (let key in this.formGroup.value) {
                        // First-time initialization or force update: store the initial value
                        let initialValue = this.formGroup.value[key];
                        this.formStateService.setInitialValue(this.uniqueId, key, initialValue);
                    }

                    // @ticket TM-1000002003: from here `dirty` means "typed while the request was in
                    // flight" — the payload has left, so screen and request are the same thing.
                    this.markPristineForInPlaceMerge();

                    this.lifecycleEvents.emit(new MvsFormEventCrudAction(currentDtoValue, "update"))
                }
                break;
            case "create":
                if (this.validateForm(true)) {
                    // this.formStateService.clearInitialValues(this.objectType);
                    this.formStateService.resetChanges(this.uniqueId); // Reset changes


                    for (let key in this.formGroup.value) {
                        // First-time initialization or force update: store the initial value
                        let initialValue = this.formGroup.value[key];
                        this.formStateService.setInitialValue(this.uniqueId, key, initialValue);
                    }
                    // @ticket TM-1000002003: see the "save" branch above.
                    this.markPristineForInPlaceMerge();

                    this.lifecycleEvents.emit(new MvsFormEventCrudAction(currentDtoValue, "create"))
                }
                break;
            case "delete":
                this.lifecycleEvents.emit(new MvsFormEventCrudAction(currentDtoValue, "delete"))
                break;
        }
    }

    onBack() {
        this.onBackEvent.emit();
    }

    /**
     * Validate Form.
     */
    validateForm(showMessage: boolean): boolean {

        // Field-level validity errors are touched-gated in mvs-form-field.component.html, so an untouched
        // mandatory field renders no error. A save/create attempt is an explicit user action and must reveal
        // ALL remaining errors at once - mark every control as touched before evaluating the form status.
        // This is the single gate for both "save" and "create"; it deliberately does not run for "delete".
        this.formGroup.markAllAsTouched();

        if (this.formGroup.status === "VALID") {
            return true;
        } else {

            // show message!
            if (showMessage) {
                this.messageService.add({severity: 'error', summary: 'Fehler', detail: "Bitte Eingaben korrigieren."})
            }

            return false;
        }

    }


    onShowHistory(event) {

        if (this.busy) {
            return
        }
        // TM-1000001467: Use panel mode instead of dialog
        this.objectHistory.showHistoryPanel(this.crudService.objectType, this.dto["id"]);
    }

    toggleAudit(event: MouseEvent) {
        this.mouseEvent = event;
        this.showAudit = !this.showAudit;
    }

    /**
     * Pass event to caller.
     * @param event
     */
    handleLifecycleEvents(event: any) {
        this.lifecycleEvents.emit(event);
    }

    handleButtonClick(event: WidgetHelperButton) {


        // do not process any actions, if one of the conditions is met
        if (this.busy || this.disabled || this.readonly || !this.complete || this.formState !== 'VALID') {
            return;
        }

        const currentDtoValue: any = this.formGroup.value;

        // inform caller
        this.lifecycleEvents.emit(new MvsFormEventFormToDto(currentDtoValue, this.formGroup));

        const mode = this.createMode ? 'create' : 'update';

        const action = new MvsFormEventCrudAction(currentDtoValue, mode)


        this.onFooterButtonClick.emit({event, action});
    }

    handleChangeFormField(formField: MvsFormFieldDto) {

        if (!formField.field) {
            return;
        }

        UiLogger.log(formField.field['id'], 'is changed');
    }

    /**
     * Fold the values the form currently holds into {@link dto}, so a rebuild of the control structure
     * renders them again instead of the last loaded row.
     *
     * <p>Only SCALAR leaves are taken over. A OneToOne sub-entity is rendered by a nested MvsFormGroup, so
     * its raw value is a plain object that is NOT the dto's own sub-entity instance; copying it over would
     * replace a typed sub-dto with an anonymous object and drop whatever the nested form did not render.
     * The nested group is rebuilt from the dto's untouched sub-entity instead.</p>
     *
     * @return whether the form was dirty at the moment of the snapshot
     */
    private preserveCurrentValuesIntoDto(): boolean {

        if (!this.formGroup) {
            return false;
        }

        const wasDirty: boolean = !!this.formGroup.dirty;
        const raw = this.formGroup.getRawValue();

        if (!raw) {
            return wasDirty;
        }

        const dto: any = this.dto ?? new DtoDetail();

        for (const key of Object.keys(raw)) {

            const value = raw[key];

            if (value !== null && typeof value === 'object' && !(value instanceof Date) && !Array.isArray(value)) {
                continue; // sub form group — see the javadoc
            }

            dto[key] = value;
        }

        this.dto = dto;

        return wasDirty;
    }

    // ── @ticket TM-1000002003 — IN-PLACE UPDATE (see the inPlaceUpdate input for the WHY) ──

    /**
     * @ticket TM-1000002003
     * Mark the group pristine when a write is DISPATCHED, so afterwards `dirty` means exactly one
     * thing: the user typed while the request was in flight. Only while {@link #inPlaceUpdate} is on.
     */
    private markPristineForInPlaceMerge(): void {

        if (!this.inPlaceUpdate || !this.formGroup) {
            return;
        }

        this.formGroup.markAsPristine();
        this.pendingInPlaceWrite = true;
    }

    /**
     * @ticket TM-1000002003
     * A write has left and has not been answered yet — see {@link #detectRejectedWrite}.
     */
    private pendingInPlaceWrite: boolean = false;

    /**
     * @ticket TM-1000002003
     * A dispatched write came back FAILED, and not as a field-validation rejection.
     *
     * <p>The formErrors branch cannot see this: {@code handleOnError} fills {@code apiErrors} only when
     * the response carries an error ARRAY, so a 500, a timeout or a network failure leaves it empty —
     * and because it is already empty after any successful save, the binding does not change at all and
     * that branch is never entered. The group would stay pristine, holding input the server never took,
     * and nothing would re-arm the autosave.</p>
     *
     * <p>Detected from the one signal that IS reliable: {@code busy} goes true → false around every
     * write, in the success and the error callback alike. A pass where it falls without a mergeable
     * {@code dto} is a write that did not land. Deliberately does not claim the pass — the existing busy
     * branch still runs and publishes, as it always did.</p>
     *
     * <p>⚠ This INFERS the failure from another component's busy handling. Nothing enforces that
     * contract; the 500 spec is what pins it, and it goes red if the contract ever moves.</p>
     */
    private detectRejectedWrite(changes: SimpleChanges): void {

        if (!this.pendingInPlaceWrite || !this.formGroup) {
            return;
        }

        const busyChange: SimpleChange = changes["busy"];

        if (!busyChange || busyChange.previousValue !== true || this.busy !== false) {
            return;
        }

        // ANY answer carrying a row clears the flag: a merge for the same id, and equally a CREATE,
        // where the id is assigned BY the write and the rebuild takes over. Only a pass with no row at
        // all is a failure — the generic assigns `dto` in its success callbacks only, so a 500, a
        // timeout or a network error never brings one.
        if (changes["dto"] && this.dto && this.dto["id"] != null) {
            this.pendingInPlaceWrite = false;
            return;
        }

        this.pendingInPlaceWrite = false;
        this.formGroup.markAsDirty();

        if (this.lastDirtyState !== true) {
            this.lastDirtyState = true;
            this.onFormDirty.emit(true);
        }

        this.emitFormState();
    }

    /**
     * @ticket TM-1000002003
     * Write the SAVED row into the live form group instead of rebuilding it.
     *
     * <p>PRISTINE → the server value wins (that is how the hidden {@code version} is updated, so the
     * next write does not fail optimistic locking). DIRTY → typed into during the flight, so the user
     * value wins and stays dirty; the autosave is then re-armed, because those keystrokes were dropped
     * by the busy gate of {@link #runAutosave}. Nothing here touches the DOM.</p>
     *
     * <h3>KNOWN LIMITATION — it relies on the control reporting itself</h3>
     * An edit made WHILE A SAVE IS IN FLIGHT in a control that pushes with
     * {@code patchValue}/{@code setValue} and never calls {@code markAsDirty} is overwritten by the
     * server value (open item on TM-1000002003). The ordinary edit-save-answer case is unaffected.
     * Sixteen controls under {@code form/field/input/} qualify — they write the group programmatically,
     * never mark dirty, and bind no {@code formControlName}: auto-complete-fkey, create-or-select,
     * document-upload, entity-context-attribute, flexible-search, general-index-search, height,
     * multi-select-string, search-or-select, select-button, select-chip, select-hierarchy, select-radio,
     * slider, ticket-type-selection, value-list-search. NOT affected, because they bind
     * {@code formControlName}: agent-pool-agent, iban-field, icon, object-type-id-search, phone-number,
     * text-field.
     */
    private mergeSavedDtoInPlace(newDto: DtoDetail): void {

        this.mergeGroupInPlace(this.formGroup, newDto);

        this.pendingInPlaceWrite = false;

        this.dto = newDto;
        this.dtoValue = this.formGroup.value;

        const dirty: boolean = !!this.formGroup.dirty;

        if (this.lastDirtyState !== dirty) {
            this.lastDirtyState = dirty;
            this.onFormDirty.emit(dirty);
        }

        // ⚠ This and onFormDirty fire synchronously inside ngOnChanges, during the host's own change
        // detection: a host that RENDERS what it reports must defer by a microtask or dev mode answers
        // NG0100.
        this.onFormChange.emit(this.formGroup.value);

        this.emitFormState();

        if (dirty && this.autoSaveMode) {
            this.autosaveTrigger.next();
        }
    }

    /**
     * @ticket TM-1000002003
     * Recursive half of {@link #mergeSavedDtoInPlace}; a {@link MvsFormGroup} control is a OneToOne
     * sub entity.
     */
    private mergeGroupInPlace(group: MvsFormGroup, dto: any): void {

        if (!group || !group.controls) {
            return;
        }

        for (const key of Object.keys(group.controls)) {

            const control = group.controls[key];

            if (control instanceof MvsFormGroup) {
                this.mergeGroupInPlace(control, dto ? dto[key] : null);
                continue;
            }

            if (control.dirty) {
                // typed into while the request was in flight — the user's value is the newer statement
                continue;
            }

            const serverValue =
                dto && Object.prototype.hasOwnProperty.call(dto, key) ? dto[key] : control.value;

            control.setValue(serverValue, {emitEvent: false});

            // a server error stamped on this control by an EARLIER answer is answered too
            control['customError'] = undefined;
            control['customInvalid'] = false;

            // no markAsPristine: it already IS pristine, and setValue does not change the flag
        }
    }


    /**
     * @ticket TM-1000002003
     * Path C — apply a server-refreshed FORM DEFINITION to the live group instead of rebuilding it.
     *
     * <p>A field marked {@code refreshFormOnChange} makes the server re-evaluate what a value implies
     * (visibility, mandatory state, computed values) and answer with a NEW MvsFormDto. Rebuilding on
     * that answer destroys the input the user is typing in — which is why this path carried a DOM
     * focus hack. When the new definition describes the SAME control structure, nothing needs
     * rebuilding: the metadata is copied onto the field objects the group already holds, the caller's
     * overwrite and the framework stamps are re-applied, and the render layer is told to re-read.</p>
     *
     * <p>Any structural difference — a field id added or removed, a sub form appearing or vanishing —
     * returns false and the untouched rebuild runs, focus hack included.</p>
     *
     * <p>Computed values from {@code dtoOnCreate} are merged on the SAME dirty rule as a saved row: a
     * pristine control takes the server value, and a control the user is typing in keeps its own, so
     * a computation for a field being edited is visibly ignored. That is deliberate — the alternative
     * is overwriting someone mid-word. Such an answer is NOT a create-mode marker: measured on
     * dev/tenantA (cr.Customer) the backend answers every round trip with the full row, edit mode
     * included, so a refusal on it would rebuild the form on every single server refresh.</p>
     *
     * <p>It also RE-ARMS the autosave when the group is still dirty, for the same reason
     * {@link #mergeSavedDtoInPlace} does: every keystroke made while the round trip was in flight was
     * dropped by the {@code templateIsRefreshing} gate and armed nothing.</p>
     *
     * @return true when the refresh was applied in place; false when the caller must rebuild.
     */
    private applyTemplateInPlace(res: MvsFormDto): boolean {

        if (!this.initialized || !this.formGroup || !res) {
            return false;
        }

        if (!this.formService.canApplyTemplateInPlace(this.formGroup, res)) {
            return false;
        }

        // Asked BEFORE the metadata copy, not after: a refusal has to leave the group exactly as it
        // was, and the copy is already a change. Same discipline as the combined dto+overwrite pass.
        if (!this.formService.canApplyOverwriteInPlace(this.formGroup, this.formControlOverwrite, this.createMode)) {
            return false;
        }

        this.formService.applyTemplateMetadataInPlace(this.formGroup, res);

        // the caller's own render decisions sit ON TOP of the server's, exactly as in a rebuild
        this.formService.applyOverwriteInPlace(this.formGroup, this.formControlOverwrite, this.createMode);

        // @ticket TM-1000002003: the answer brings VALUES as well as metadata — the server recomputes
        // mirror and derived fields on every round trip and hands the whole row back on `dtoOnCreate`.
        // The `dto` bookkeeping is the old handler's, verbatim; what is new is that the live controls
        // are merged rather than rebuilt from it, under the SAME dirty rule as a saved row.
        //
        // The controls see the MERGED dto, never the raw answer: `mergeDtos` keeps the row's own value
        // wherever the answer carries null or undefined, and the group has to be given exactly what the
        // rebuild would have been given. Handing it `res.dtoOnCreate` instead blanked every free-text
        // field the server does not recompute — the mount-time refresh answers those as undefined, and
        // `mergeGroupInPlace` writes any key the object HAS. Measured live on cw document 7354, field
        // gen_17. Note that `mergeGroupInPlace` itself must keep writing null: on the saved-row path a
        // null IS the server's statement that the field was cleared.
        if (res.dtoOnCreate) {
            this.dto = DtoHelper.mergeDtos(this.dto, res.dtoOnCreate, res);
            this.mergeGroupInPlace(this.formGroup, this.dto);
        }

        this.form = res;
        this.complete = res.formComplete;

        this.dtoValue = this.formGroup.value;

        // The round trip is OVER, and these flags are what emitFormState reports. Clearing them here
        // rather than in the caller is the whole point: the old path clears busy BEFORE it publishes,
        // and a snapshot published while busy is still true is never corrected — emitFormState only
        // fires on a CHANGED state, so the host would read „saving" for the rest of the form's life.
        this.busy = false;
        this.busyFormMode = null;
        this.templateIsRefreshing = false;

        // mirror refreshComponent's bookkeeping: publish dirty only when it really moved
        if (this.lastDirtyState === null) {
            this.lastDirtyState = this.formGroup.dirty;
        } else if (this.lastDirtyState !== this.formGroup.dirty) {
            this.lastDirtyState = this.formGroup.dirty;
            this.onFormDirty.emit(this.formGroup.dirty);
        }

        this.onFormChange.emit(this.formGroup.value);
        this.emitFormState();

        // @ticket TM-1000002003: keystrokes made WHILE the round trip was in flight armed nothing —
        // the form-group valueChanges handler skips its whole body, autosave included, while
        // `templateIsRefreshing` is up, and `runAutosave` refuses for the same reason. Without this the
        // group stays dirty and nothing ever writes it. Mirrors mergeSavedDtoInPlace.
        if (this.autoSaveMode && this.formGroup.dirty) {
            this.autosaveTrigger.next();
        }

        return true;
    }

    /**
     * @ticket TM-1000002003
     * Try to answer this change WITHOUT rebuilding the form group.
     *
     * <p>Three transitions: the overwrite alone, a saved row alone, or BOTH in one pass — the normal
     * shape of the cw consultation document, whose host re-derives its overwrite from
     * {@code onChangedObject}, fired synchronously after {@code dto} was assigned. Order: the
     * read-only structural check FIRST, because a refusal must leave the group untouched.</p>
     *
     * @return true when the change was fully handled in place.
     */
    private handleInPlaceChange(changes: SimpleChanges): boolean {

        if (!this.formGroup) {
            return false;
        }

        this.detectRejectedWrite(changes);

        // New form metadata or a new import context is new DATA, not a render decision and not an
        // answer to a write of ours. Nothing in place can express it.
        // @ticket TM-1000002003: this guard is ALSO what routes a row switch to the rebuild. The only
        // component that mounts this one is mvs-crud-object-generic, and every path of it that hands
        // down a different row assigns `form` in the same pass (the load and the create answer); the
        // lone dto-only assignment is the update answer, which is by definition the same row.
        // ⚠ The defence-in-depth id check that used to sit below was removed by decision, so a dto for
        // a DIFFERENT row arriving WITHOUT a form would now be merged into the live group rather than
        // refused. Reachable only from a second mount site, which does not exist; the behaviour is
        // pinned in mvs-crud-object-form.in-place-merge.spec.ts („merges a different-id dto when no
        // form change accompanies it"), and that spec is what to invert if one ever appears.
        if (changes["form"] || changes["importObjectContextDto"]) {
            return false;
        }

        const overwriteChanged: boolean = !!changes["formControlOverwrite"];
        const dtoChanged: boolean = !!changes["dto"];

        if (!overwriteChanged && !dtoChanged) {
            return false;
        }

        // Nothing to merge FROM. The row-switch case is already handled by the `form` guard above.
        if (dtoChanged && !this.dto) {
            return false;
        }

        // Asked BEFORE anything is written, so a refusal leaves the group exactly as it was. An
        // EXCLUDED toggle adds or removes a control, which no in-place path can do.
        // @ticket TM-1000002003: createMode is NOT optional. Without it the check treats the legitimately
        // absent create-mode id control as a removed one and refuses, so every create-capable mount
        // rebuilds on a visibility change.
        if (overwriteChanged
            && !this.formService.canApplyOverwriteInPlace(this.formGroup, this.formControlOverwrite, this.createMode)) {
            return false;
        }

        if (dtoChanged) {
            this.mergeSavedDtoInPlace(<DtoDetail>this.dto);
        }

        if (overwriteChanged) {
            // Cannot refuse any more — canApplyOverwriteInPlace above already cleared it.
            this.formService.applyOverwriteInPlace(this.formGroup, this.formControlOverwrite, this.createMode);
            this.emitFormState();
        }

        return true;
    }

    /**
     * Process Changes.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {

        if (!this.initialized) {
            return;
        }

        // @ticket TM-1000002003: one guarded call; everything new lives in the method.
        if (this.inPlaceUpdate && this.handleInPlaceChange(changes)) {
            return;
        }

        // check whether objectId changed and load object details
        if (changes["dto"] || changes["importObjectContextDto"] || changes["form"] || changes["formControlOverwrite"]) {

            // An access overwrite arriving on an ALREADY RENDERED form is a render decision, not new data.
            // The rebuild below builds the controls from `this.dto`, which is the row as it was last loaded
            // or saved — so a caller that changes the overwrite while the user is filling the form used to
            // silently discard everything typed since. Fold the current control values into the dto first;
            // same preserve-by-raw-value technique verifyValidityField() uses. Only when the overwrite is
            // the sole change: a new `dto` or `form` IS new data and must win.
            const preservedDirty: boolean =
                changes["formControlOverwrite"] && !changes["dto"] && !changes["form"]
                    ? this.preserveCurrentValuesIntoDto()
                    : false;

            this.refreshComponent(false);

            // A rebuilt group is pristine. Without this the preserved-but-unsaved values would be carried
            // by a form that reports itself as clean, and every consumer that flushes "the dirty forms" on
            // its own submit would skip it — the values would survive the rebuild and be lost at save time
            // instead, which is worse than losing them here because nothing on screen shows it.
            if (preservedDirty && this.formGroup && !this.formGroup.dirty) {
                this.formGroup.markAsDirty();
                this.lastDirtyState = true;
                this.onFormDirty.emit(true);
                this.emitFormState();
            }
        }
        if (changes["formErrors"]) {
            this.setFormErrors(this.formErrors);
        }
        if (changes["busy"] || changes["disabled"] || changes["readonly"] || changes["createMode"]) {
            this.emitFormState();
        }
    }

    /**
     * Whether a save/create may be triggered right now.
     *
     * The predicate is deliberately the SAME one the built-in create/save buttons use in the
     * template, so a consumer-owned action button can never be enabled while the built-in one
     * would have been disabled — and {@link #triggerAction} enforces exactly this.
     */
    public isSubmittable(): boolean {
        return !!this.complete && !this.busy && !this.disabled && !!this.formGroup?.valid;
    }

    /**
     * Trigger the very same write the built-in "Anlegen" / "Speichern" footer button performs, from
     * OUTSIDE the form. This is what makes {@link #hideDefaultActions} usable without the caller
     * rebuilding the write itself: the form still owns the payload (formGroup.value, including every
     * generic attribute it rendered), the CRUD service call and the resulting
     * onChangedObject / onObjectChangeError events.
     *
     * @param action  'create' or 'save'; defaults to the form's own mode, which is what a caller
     *                that just wants "save it now" should use.
     * @return true if the write was started. false means the form refused — read
     *         {@link #onFormStateChange} to learn why. A refusal caused purely by field validation
     *         marks all controls as touched and shows the standard error toast, exactly like a
     *         click on the built-in button would.
     */
    public triggerAction(action?: 'create' | 'save'): boolean {

        if (!this.initialized || !this.formGroup) {
            return false;
        }

        // Same gate as onAction() — checked here as well so the caller gets a verdict instead of a
        // silent no-op.
        if (this.busy || this.disabled || this.readonly || !this.complete) {
            return false;
        }

        if (!this.formGroup.valid) {
            // reveal every remaining field error at once — the caller pressed its own save button
            this.validateForm(true);
            return false;
        }

        this.onAction(action ?? (this.createMode ? 'create' : 'save'));

        return true;
    }

    /**
     * Publish {@link #onFormSubmitStateChange} and {@link #onFormStateChange} when they changed.
     */
    private emitFormState(): void {

        const submittable = this.isSubmittable();

        if (submittable !== this.lastSubmitState) {
            this.lastSubmitState = submittable;
            this.onFormSubmitStateChange.emit(submittable);
        }

        const state = MvsFormStateDto.create({
            dirty: !!this.formGroup?.dirty,
            valid: !!this.formGroup?.valid,
            complete: !!this.complete,
            busy: !!this.busy,
            disabled: !!this.disabled,
            readonly: !!this.readonly,
            createMode: !!this.createMode,
            submittable: submittable
        });

        if (state.equals(this.lastFormState)) {
            return;
        }

        this.lastFormState = state;
        this.onFormStateChange.emit(state);
    }

    getChangedValue(changedObject: DtoDetail) {
        for (let item in changedObject) {
            const currentValue = this.dtoValue[item];
            const newValue = changedObject[item];
            if (currentValue != newValue) {
                this.dtoValue = changedObject;
                return item;
            }
        }
        return null;
    }

    /**
     * Clean up.
     */
    ngOnDestroy(): void {
        this.formStateService.clearInitialValues(this.objectType);
        this.destroyFormGroup();

        if (this.autosaveSub) {
            this.autosaveSub.unsubscribe();
        }

    }

    /**
     * Map backend validation codes to Angular validators and apply them to the FormControl.
     */
    applyValidatorsAndErrors(fieldControl: AbstractControl, errorMessage: string, code: string) {


        // Map backend validation code to Angular validators
        const validators = this.mapValidators(code);

        // Only a code mapValidators actually KNOWS may re-stamp the control. setValidators REPLACES
        // the whole set and mapValidators answers every code outside its switch with an empty one, so
        // calling it unconditionally strips the validators buildFormGroup attached from the template
        // metadata until the next form rebuild — purely because the server reported a code from
        // outside that vocabulary. A known code still takes the exact same path.
        if (validators.length) {
            fieldControl.setValidators(validators);
        }

        // Apply server-side errors if any
        this.applyServerErrors(fieldControl, errorMessage);

    }


    private applyServerErrors(fieldControl: AbstractControl, errorMessage: string) {

        // created a new field customError in control because the error field was overwriting itself
        fieldControl['customError'] = errorMessage
        fieldControl['customInvalid'] = true;
    }


    mapValidators(validationCode: string): ValidatorFn[] {
        const validatorSet = new Set<ValidatorFn>();

        switch (validationCode) {
            case 'required':
            case 'notEmpty':
            case 'mandatory':
            case 'notNull':
            case 'wrong':
                validatorSet.add(Validators.required);
                break;
            case 'invalid.email':
                validatorSet.add(Validators.email);
                break;
            // Add more cases as needed based on backend codes
            default:
                break;
        }

        return Array.from(validatorSet);
    }

    handleConfirmationFieldRefresh(event: DtoTemplateOptions) {
        const value = this.formGroup?.getRawValue();
        this.refreshTemplate(false, value, true, event);
    }

    handleAgentMode() {
        // this.formMode = FormModeEnum.SMART;

        const options = new DtoTemplateOptions();
        options.formMode = FormModeEnum.SMART;
        this.refreshTemplate(false, this.formGroup?.getRawValue(), true, options);

    }

    handleAgentConfigMode() {
        // this.formMode = FormModeEnum.SMART_CONFIG;

        const options = new DtoTemplateOptions();
        options.formMode = FormModeEnum.SMART_CONFIG;
        this.refreshTemplate(false, this.formGroup?.getRawValue(), true, options);

        this.getAllUseCases();

    }

    getAllUseCases() {
        this.sgUseCaseService.list(new ObjectRequestList(false, [], [])).subscribe(res => {
            this.sgUseCases = res.entries;
        });
    }

    onUseCaseChange(event: SelectChangeEvent) {
        const options = new DtoTemplateOptions();
        options.formMode = FormModeEnum.SMART_CONFIG;
        options.sgUseCaseId = event.value;
        this.refreshTemplate(false, this.formGroup?.getRawValue(), true, options, event.value);
    }

    handleNormalMode() {
        const options = new DtoTemplateOptions();
        options.formMode = FormModeEnum.REGULAR;
        this.refreshTemplate(false, this.formGroup?.getRawValue(), true, options);
    }

    handleFormTargetFocus(event: FormSmartGuideFocusEvent) {
        this.onFormTargetFocus.emit(event);
    }

    getAvailableModes() {
        const modes = [];
        modes.push({label: 'Regular Mode', value: FormModeEnum.REGULAR});
        if (this.sgUseCaseId) {
            modes.push({label: 'Smart Mode', value: FormModeEnum.SMART});
        }
        modes.push({label: 'Smart Config Mode', value: FormModeEnum.SMART_CONFIG});

        return modes;
    }


    onFormModeChange(mode: FormModeEnum) {
        this.busyFormMode = mode;
        switch (mode) {
            case FormModeEnum.SMART:
                this.handleAgentMode();
                break;
            case FormModeEnum.REGULAR:
                this.handleNormalMode();
                break;
            case FormModeEnum.SMART_CONFIG:
                this.handleAgentConfigMode();
                break;
        }
    }

    handleCustomButtonClick(button: FormFooterButton) {
        if (button.command) {
            button.command(button);
        }

        // Only reachable while the built-in action buttons are suppressed: a custom button may then
        // take over one of them by declaring its action. Guarded by hideDefaultActions so the
        // behaviour of every existing consumer stays unchanged.
        if (this.hideDefaultActions && (button.action === 'create' || button.action === 'save')) {
            this.onAction(button.action);
        }
    }

    protected readonly FormModeEnum = FormModeEnum;
}
